import { useState } from 'react';
import { CreditTransaction } from '../../lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import { formatTransactionAmount, formatCreditsWithDollar } from '@/lib/format-credits';

interface TransactionHistoryProps {
  transactions: CreditTransaction[];
  total: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

function getTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    grant: 'Credit Grant',
    system_grant: 'System Credits',
    welcome_grant: 'Welcome Bonus',
    subscription_reset: 'Subscription Reset',
    system_reset: 'Monthly Reset',
    purchase: 'Purchase',
    subscription_credit: 'Subscription',
    usage: 'Usage',
    refund: 'Refund',
    adjustment: 'Adjustment',
    storage_charge: 'Storage',
    file_operation: 'File Op',
    api_usage: 'API Usage',
  };
  return labels[type] ?? type;
}

function getBucketLabel(bucket: string | null | undefined): string | null {
  if (!bucket) return null;
  const labels: Record<string, string> = {
    subscription: 'Subscription',
    fias: 'Fias',
    system: 'System',
  };
  return labels[bucket] ?? null;
}

function getBucketStyles(bucket: string | null | undefined): string {
  switch (bucket) {
    case 'subscription':
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
    case 'fias':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'system':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function getTypeStyles(type: string): string {
  switch (type) {
    case 'grant':
    case 'system_grant':
    case 'purchase':
    case 'subscription_credit':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'usage':
    case 'storage_charge':
    case 'file_operation':
    case 'api_usage':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'refund':
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function getCategoryLabel(category: string | null): string {
  if (!category) return '';
  const labels: Record<string, string> = {
    ai_chat: 'AI Chat',
    ai_multi: 'Multi-AI',
    ai_image: 'Image Gen',
    ai_builder: 'Plugin Builder',
    ai_social: 'Social Media AI',
    ai_learn: 'Learning',
    ai_calendar: 'Calendar AI',
    ai_other: 'AI',
    storage: 'Storage',
    file_ops: 'Files',
    publishing: 'Publishing',
    api_external: 'External API',
  };
  return labels[category] ?? category;
}

// =============================================================================
// Data Grouping
// =============================================================================

interface CollapsedTransaction {
  id: string;
  type: string;
  netAmount: number;
  balanceAfter: number;
  description: string;
  createdAt: string;
  requestId: string | null;
  expenseCategory: string | null;
  sessionId: string | null;
  bucket: string | null;
  modelLabel: string | null;
  rawEntries: CreditTransaction[];
}

function extractModelLabel(txn: CreditTransaction): string | null {
  const provider = txn.metadata?.provider as string | undefined;
  const model = txn.metadata?.model as string | undefined;
  if (provider && model) return `${provider}/${model}`;
  if (model) return model;
  return null;
}

interface SessionGroup {
  sessionId: string;
  label: string;
  category: string | null;
  totalAmount: number;
  startedAt: string;
  transactions: CollapsedTransaction[];
}

function collapseReserveRefundPairs(transactions: CreditTransaction[]): CollapsedTransaction[] {
  // Group every reserve/refund row that shares a request_id into a single
  // collapsed entry. A request_id can hold N usage rows and M finalization
  // refund rows when the reservation was split across credit buckets, so
  // pairing 1:1 by request_id is wrong — we need to net the whole group.
  // Standalone (non-finalization) refunds and rows without a request_id
  // pass through unchanged.
  const result: CollapsedTransaction[] = [];
  const groupsByRequestId = new Map<string, CreditTransaction[]>();
  const consumedTxnIds = new Set<string>();

  for (const txn of transactions) {
    if (!txn.requestId) continue;
    if (txn.type === 'usage') {
      if (!groupsByRequestId.has(txn.requestId)) groupsByRequestId.set(txn.requestId, []);
      groupsByRequestId.get(txn.requestId)!.push(txn);
      consumedTxnIds.add(txn.transactionId);
    } else if (
      txn.type === 'refund' &&
      (txn.description?.includes('finalization') || txn.description?.includes('cancelled'))
    ) {
      if (!groupsByRequestId.has(txn.requestId)) groupsByRequestId.set(txn.requestId, []);
      groupsByRequestId.get(txn.requestId)!.push(txn);
      consumedTxnIds.add(txn.transactionId);
    }
  }

  // Drop groups that don't actually have a usage row to anchor on — those
  // refunds should fall back to standalone display.
  for (const [requestId, group] of groupsByRequestId) {
    const hasUsage = group.some((t) => t.type === 'usage');
    if (!hasUsage) {
      for (const t of group) consumedTxnIds.delete(t.transactionId);
      groupsByRequestId.delete(requestId);
    }
  }

  // Anchor each collapsed entry on the first usage row in original order so
  // it slots into the right chronological position.
  const collapsedByAnchorTxnId = new Map<string, CollapsedTransaction>();
  for (const [requestId, group] of groupsByRequestId) {
    const usageRows = group.filter((t) => t.type === 'usage');
    const anchor = usageRows[0];

    const totalUsage = group
      .filter((t) => t.type === 'usage')
      .reduce((sum, t) => sum + t.amount, 0); // negative
    const totalRefund = group
      .filter((t) => t.type === 'refund')
      .reduce((sum, t) => sum + t.amount, 0); // positive

    const lastRow = group.reduce((latest, t) =>
      new Date(t.createdAt).getTime() > new Date(latest.createdAt).getTime() ? t : latest,
    );

    let description = anchor.description ?? '';
    description = description.replace(/\s*\((reserved|finalized|cancelled)[^)]*\)/, '');

    collapsedByAnchorTxnId.set(anchor.transactionId, {
      id: anchor.transactionId,
      type: 'usage',
      netAmount: totalUsage + totalRefund,
      balanceAfter: lastRow.balanceAfter,
      description,
      createdAt: anchor.createdAt,
      requestId,
      expenseCategory: anchor.expenseCategory,
      sessionId: (anchor.metadata?.sessionId as string) ?? null,
      bucket: anchor.bucket ?? null,
      modelLabel: extractModelLabel(anchor),
      rawEntries: group,
    });
  }

  // Walk the original list once, emitting the collapsed entry on its anchor
  // row and skipping every other consumed row.
  for (const txn of transactions) {
    const collapsed = collapsedByAnchorTxnId.get(txn.transactionId);
    if (collapsed) {
      result.push(collapsed);
      continue;
    }
    if (consumedTxnIds.has(txn.transactionId)) continue;
    result.push({
      id: txn.transactionId,
      type: txn.type,
      netAmount: txn.amount,
      balanceAfter: txn.balanceAfter,
      description: txn.description ?? '',
      createdAt: txn.createdAt,
      requestId: txn.requestId,
      expenseCategory: txn.expenseCategory,
      sessionId: (txn.metadata?.sessionId as string) ?? null,
      bucket: txn.bucket ?? null,
      modelLabel: extractModelLabel(txn),
      rawEntries: [txn],
    });
  }
  return result;
}

function groupIntoSessions(
  collapsed: CollapsedTransaction[],
): (SessionGroup | CollapsedTransaction)[] {
  const result: (SessionGroup | CollapsedTransaction)[] = [];
  const sessionMap = new Map<string, CollapsedTransaction[]>();
  const order: (string | CollapsedTransaction)[] = [];
  const seenSessions = new Set<string>();

  for (const txn of collapsed) {
    if (txn.sessionId) {
      if (!sessionMap.has(txn.sessionId)) {
        sessionMap.set(txn.sessionId, []);
      }
      sessionMap.get(txn.sessionId)!.push(txn);
      if (!seenSessions.has(txn.sessionId)) {
        seenSessions.add(txn.sessionId);
        order.push(txn.sessionId);
      }
    } else {
      order.push(txn);
    }
  }

  for (const item of order) {
    if (typeof item === 'string') {
      const txns = sessionMap.get(item)!;
      const totalAmount = txns.reduce((sum, t) => sum + t.netAmount, 0);
      const category = txns[0].expenseCategory;
      const categoryLabel = getCategoryLabel(category);
      const label = categoryLabel ? `${categoryLabel} Session` : `Session`;

      result.push({
        sessionId: item,
        label,
        category,
        totalAmount,
        startedAt: txns[0].createdAt,
        transactions: txns,
      });
    } else {
      result.push(item);
    }
  }

  return result;
}

function isSessionGroup(item: SessionGroup | CollapsedTransaction): item is SessionGroup {
  return 'transactions' in item && 'label' in item;
}

// =============================================================================
// Sub-Components
// =============================================================================

function getRawEntryLabel(txn: CreditTransaction, isCollapsedChild: boolean): string {
  if (!isCollapsedChild) return getTypeLabel(txn.type);
  // Inside a collapsed reserve/refund pair, use clearer labels
  if (txn.type === 'usage') return 'Estimate';
  if (txn.type === 'refund') return 'Adjustment';
  return getTypeLabel(txn.type);
}

function RawEntryRow({
  txn,
  isCollapsedChild = false,
}: {
  txn: CreditTransaction;
  isCollapsedChild?: boolean;
}) {
  const label = getRawEntryLabel(txn, isCollapsedChild);
  const modelLabel = extractModelLabel(txn);
  return (
    <div className="flex items-center justify-between py-1.5 text-xs text-muted-foreground">
      <div className="flex items-center gap-2">
        <span
          className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getTypeStyles(txn.type)}`}
        >
          {label}
        </span>
        {modelLabel && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
            {modelLabel}
          </span>
        )}
        <span>{new Date(txn.createdAt).toLocaleTimeString()}</span>
      </div>
      <span
        className={
          txn.amount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
        }
      >
        {formatTransactionAmount(txn.amount)}
      </span>
    </div>
  );
}

function TransactionRow({ txn }: { txn: CollapsedTransaction }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = txn.rawEntries.length > 1;

  return (
    <div className="py-2">
      <button
        onClick={() => hasDetail && setExpanded((v) => !v)}
        className={`w-full flex items-start justify-between gap-4 text-left ${hasDetail ? 'cursor-pointer hover:bg-muted/30 -mx-2 px-2 rounded' : ''}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap ${getTypeStyles(txn.type)}`}
            >
              {getTypeLabel(txn.type)}
            </span>
            <span className="text-xs text-muted-foreground">
              {new Date(txn.createdAt).toLocaleTimeString()}
            </span>
            {hasDetail && (
              <span className="text-[10px] text-muted-foreground/60">
                {expanded ? '[-]' : '[+]'}
              </span>
            )}
          </div>
          {txn.description && (
            <p className="text-sm text-muted-foreground truncate mt-0.5">{txn.description}</p>
          )}
        </div>
        <span
          className={`text-sm font-semibold shrink-0 ${txn.netAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
        >
          {formatTransactionAmount(txn.netAmount)}
        </span>
      </button>
      {expanded && (
        <div className="ml-6 mt-1 pl-3 border-l-2 border-border/50">
          {txn.rawEntries.map((raw) => (
            <RawEntryRow key={raw.transactionId} txn={raw} isCollapsedChild />
          ))}
        </div>
      )}
    </div>
  );
}

function SessionGroupRow({ session }: { session: SessionGroup }) {
  const [expanded, setExpanded] = useState(false);
  // Sum the underlying ledger rows wrapped by every collapsed entry inside
  // the session, so the displayed total reconciles with the header's "ledger
  // entries" count.
  const ledgerEntryCount = session.transactions.reduce((sum, t) => sum + t.rawEntries.length, 0);

  return (
    <div className="p-4 hover:bg-muted/50">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start justify-between gap-4 text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">{session.label}</span>
            <span className="text-xs text-muted-foreground">
              {session.transactions.length} {session.transactions.length === 1 ? 'call' : 'calls'}
            </span>
            {ledgerEntryCount > session.transactions.length && (
              <span
                className="text-[10px] text-muted-foreground/60"
                title="Underlying ledger entries — each call may write multiple rows for reservation, finalization, and per-bucket splits."
              >
                {ledgerEntryCount} entries
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {new Date(session.startedAt).toLocaleString()}
            </span>
            <span className="text-[10px] text-muted-foreground/60">{expanded ? '[-]' : '[+]'}</span>
          </div>
        </div>
        <span
          className={`text-sm font-semibold shrink-0 ${session.totalAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
        >
          {formatTransactionAmount(session.totalAmount)}
        </span>
      </button>
      {expanded && (
        <div className="ml-4 mt-2 pl-3 border-l-2 border-border/50 space-y-0.5">
          {session.transactions.map((txn) => (
            <TransactionRow key={txn.id} txn={txn} />
          ))}
        </div>
      )}
    </div>
  );
}

function StandaloneRow({ txn }: { txn: CollapsedTransaction }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = txn.rawEntries.length > 1;

  return (
    <div className="p-4 hover:bg-muted/50">
      <button
        onClick={() => hasDetail && setExpanded((v) => !v)}
        className={`w-full flex items-start justify-between gap-4 text-left ${hasDetail ? 'cursor-pointer' : ''}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap ${getTypeStyles(txn.type)}`}
            >
              {getTypeLabel(txn.type)}
            </span>
            {getBucketLabel(txn.bucket) && (
              <span
                className={`px-1.5 py-0.5 text-[10px] font-medium rounded whitespace-nowrap ${getBucketStyles(txn.bucket)}`}
                title={`${getBucketLabel(txn.bucket)} bucket`}
              >
                {getBucketLabel(txn.bucket)}
              </span>
            )}
            {txn.modelLabel && (
              <span
                className="px-1.5 py-0.5 text-[10px] font-medium rounded whitespace-nowrap bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400"
                title={`Model: ${txn.modelLabel}`}
              >
                {txn.modelLabel}
              </span>
            )}
            {txn.expenseCategory && (
              <span className="text-xs text-muted-foreground">
                {getCategoryLabel(txn.expenseCategory)}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {new Date(txn.createdAt).toLocaleString()}
            </span>
            {hasDetail && (
              <>
                <span
                  className="text-[10px] text-muted-foreground/60"
                  title="Underlying ledger entries — this row collapses a usage row plus its finalization-refund rows (one per credit bucket touched) into a single net entry."
                >
                  {txn.rawEntries.length} entries
                </span>
                <span className="text-[10px] text-muted-foreground/60">
                  {expanded ? '[-]' : '[+]'}
                </span>
              </>
            )}
          </div>
          {txn.description && (
            <p className="text-sm text-muted-foreground truncate mt-0.5">{txn.description}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <span
            className={`text-sm font-semibold ${txn.netAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
          >
            {formatTransactionAmount(txn.netAmount)}
          </span>
          <div className="text-xs text-muted-foreground">
            Bal: {formatCreditsWithDollar(txn.balanceAfter)}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="ml-6 mt-1 pl-3 border-l-2 border-border/50">
          {txn.rawEntries.map((raw) => (
            <RawEntryRow key={raw.transactionId} txn={raw} isCollapsedChild />
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

// System bookkeeping transaction types that are hidden from non-admin users.
// Usage and refund rows from the system bucket ARE shown — they represent
// real user-visible activity (Learn About Fias, Customer Support, etc.).
const SYSTEM_BOOKKEEPING_TYPES = new Set(['system_grant', 'system_reset']);

function isSystemBookkeeping(txn: CreditTransaction): boolean {
  if (SYSTEM_BOOKKEEPING_TYPES.has(txn.type)) return true;
  return false;
}

export function TransactionHistory({
  transactions,
  total,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
}: TransactionHistoryProps) {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('admin') ?? false;

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-48 mb-4"></div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="py-4 border-b border-border last:border-0">
              <div className="flex justify-between items-center">
                <div className="h-4 bg-muted rounded w-32"></div>
                <div className="h-4 bg-muted rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // System bookkeeping (grants, resets) is hidden from non-admin users.
  // System-bucket usage/refund rows are visible — they represent real activity.
  const visibleTransactions = isAdmin
    ? transactions
    : transactions.filter((txn) => !isSystemBookkeeping(txn));
  const hiddenCount = transactions.length - visibleTransactions.length;
  const visibleTotal = Math.max(0, total - hiddenCount);

  const collapsed = collapseReserveRefundPairs(visibleTransactions);
  const grouped = groupIntoSessions(collapsed);

  // The header used to show "(13 total)", which was the raw ledger row count
  // from the server. After collapse + session grouping the user might see only
  // 3 visible items, so the unit mismatch made it look like rows were missing.
  // Show both numbers — the count of visible events the user can actually
  // count on screen, and the count of underlying ledger entries those events
  // represent — so the relationship is obvious. When everything reconciles to
  // a single number (no grouping happened), collapse to one number.
  const eventCount = grouped.length;
  const showBothCounts = eventCount !== visibleTotal;
  const headerCount = showBothCounts
    ? `${eventCount} ${eventCount === 1 ? 'event' : 'events'} · ${visibleTotal} ledger entries`
    : `${visibleTotal} total`;

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
      <div className="p-6 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">
          Transaction History
          <span
            className="text-sm font-normal text-muted-foreground ml-2"
            title={
              showBothCounts
                ? 'A single user action (an AI call, a refund, a grant) often writes ' +
                  'multiple rows to the underlying ledger — for example a billed AI ' +
                  'call writes one usage row plus one finalization-refund row per ' +
                  'credit bucket it touched. Events are what you see; ledger entries ' +
                  'are the underlying audit rows.'
                : undefined
            }
          >
            ({headerCount})
          </span>
        </h2>
      </div>

      {grouped.length === 0 ? (
        <div className="p-6 text-center text-muted-foreground">No transactions yet</div>
      ) : (
        <div className="divide-y divide-border">
          {grouped.map((item) =>
            isSessionGroup(item) ? (
              <SessionGroupRow key={item.sessionId} session={item} />
            ) : (
              <StandaloneRow key={item.id} txn={item} />
            ),
          )}
        </div>
      )}

      {hasMore && (
        <div className="p-4 border-t border-border text-center">
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="px-4 py-2 text-sm font-medium text-primary hover:text-primary/80 disabled:text-muted-foreground"
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
}
